import { useEffect } from 'react';
import { CrossIcon, InformationCircle, Mail, Pencil, Plus } from 'assets/icons';
import UseContext from 'hooks/useContext';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import CopyToClipboard from 'components/copyToClipboard';
import UseModal from 'components/modal/useModal';
import { COMPANY_ADMIN_ROLE, companyRoleLabel } from 'constants/companyRoles';

const PENDING_VALIDATION = 0;

const SlideOverCompanyMembers = ({
  companyInfo,
  setCompanyInfo,
  onEditMember,
}) => {
  const { toggle } = UseModal();
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const t = (key) => language.getValue(`companyContacts.registered.${key}`, '');
  // A company without members can come back as `employees: [{}]`: an entry
  // without an id is nobody, neither a row nor a count.
  const employees = (companyInfo?.employees ?? []).filter(
    (entry) => entry.id_employee,
  );

  const openModal = (type, data) => {
    setModalData(data);
    toggle(type);
  };

  // The removal modal warns about the last administrator only: with another one
  // left, the reminders of the company still reach someone. Same rule as the
  // contact edit screen: an entry without an account receives no reminder.
  const hasOtherAdminThan = (member) =>
    employees.some(
      (entry) =>
        Number(entry.role) === COMPANY_ADMIN_ROLE &&
        entry.user &&
        entry.user._id !== member._id,
    );

  useEffect(() => {
    if (modalDataReturn?.type === 'employee_created' && modalDataReturn?.user) {
      setCompanyInfo((previous) => ({
        ...previous,
        employees: [
          ...(previous.employees ?? []),
          {
            id_employee: modalDataReturn.user._id,
            role: modalDataReturn.user.role,
            user: modalDataReturn.user,
          },
        ],
      }));
      setModalData({});
      setModalDataReturn({});
    }
    if (
      modalDataReturn?.type === 'employee_deleted' &&
      modalDataReturn?.idEmployee
    ) {
      setCompanyInfo((previous) => ({
        ...previous,
        // By the entry's id: a row without an account has no user to match.
        employees: (previous.employees ?? []).filter(
          (entry) =>
            String(entry.id_employee ?? entry.user?._id) !==
            modalDataReturn.idEmployee,
        ),
      }));
      setModalData({});
      setModalDataReturn({});
    }
  }, [modalDataReturn, setCompanyInfo, setModalData, setModalDataReturn]);

  return (
    <div className={'flex flex-col'}>
      <div className={'flex gap-1 justify-between items-center pb-2'}>
        <div className={'flex gap-2 items-center'}>
          <div className={'font-semibold'}>{t('title')}</div>
          <div
            className={
              'my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]'
            }
          >
            {employees.length}
          </div>
        </div>
        <Button
          color={'SECONDARY'}
          size={'SM'}
          onClick={() =>
            openModal('add_company_member', { idCompany: companyInfo?._id })
          }
        >
          <div className={'flex gap-1 items-center'}>
            <Plus wh={20} color={'#374151'} />
            <span>{t('add')}</span>
          </div>
        </Button>
      </div>
      {employees.map((entry) => {
        const user = entry.user;
        if (!user) {
          return (
            <div
              key={String(entry.id_employee)}
              className={
                'py-2 border-b flex justify-between gap-2 items-center'
              }
            >
              <div className={'text-gray-500'}>{t('missingAccount')}</div>
              <div className={'w-28 flex items-center justify-end'}>
                <Button
                  size={'ICON'}
                  color={'ICON'}
                  onClick={() =>
                    openModal('delete_company_member', {
                      idCompany: companyInfo?._id,
                      idEmployee: String(entry.id_employee),
                      missingAccount: true,
                    })
                  }
                >
                  <CrossIcon wh={20} color={'#111827'} />
                  <span className={'sr-only'}>{t('removeMissingAccount')}</span>
                </Button>
              </div>
            </div>
          );
        }
        const isPending = user.validation === PENDING_VALIDATION;
        return (
          <div
            key={user._id}
            className={'py-2 border-b flex justify-between gap-2 items-center'}
          >
            <div className={'min-w-0'}>
              <div className={'flex gap-1'}>
                <CopyToClipboard text={user.firstname ?? ''}>
                  {user.firstname ?? ''}
                </CopyToClipboard>
                <CopyToClipboard text={user.lastname ?? ''}>
                  {(user.lastname ?? '').toUpperCase()}
                </CopyToClipboard>
              </div>
              {/* CopyToClipboard never shrinks by itself and its own truncate
                  cannot paint an ellipsis around inline text: the items get
                  min-w-0 and the value a block child with truncate, so a long
                  address is cut with an ellipsis instead of running under the
                  role column. */}
              <div
                className={'text-gray-500 flex gap-1 flex-wrap [&>*]:min-w-0'}
              >
                <CopyToClipboard text={user.email ?? ''}>
                  <div className={'truncate'} title={user.email ?? ''}>
                    {user.email ?? ''}
                  </div>
                </CopyToClipboard>
                {user.phone ? (
                  <>
                    <span>·</span>
                    <CopyToClipboard text={user.phone}>
                      <div className={'truncate'} title={user.phone}>
                        {user.phone}
                      </div>
                    </CopyToClipboard>
                  </>
                ) : null}
              </div>
            </div>
            <div className={'flex gap-2 items-center shrink-0'}>
              {isPending ? (
                <div
                  className={'text-xs text-gray-500 flex gap-1 items-center'}
                >
                  <InformationCircle wh={14} color={'#6B7280'} />
                  {t('pending')}
                </div>
              ) : null}
              {/* Fixed width so every badge starts at the same x, whatever the
                  role's length and whether the row carries the resend action. */}
              <div
                className={'w-32 flex justify-start'}
                data-testid={'role-badge-column'}
              >
                <Badge type={'SECONDARY'}>
                  {companyRoleLabel(language, entry.role)}
                </Badge>
              </div>
              <div className={'w-28 flex items-center justify-end'}>
                {isPending ? (
                  <Button
                    size={'ICON'}
                    color={'ICON'}
                    onClick={() =>
                      openModal('send_mail_link_company_member', {
                        idCompany: companyInfo?._id,
                        idEmployee: user._id,
                      })
                    }
                  >
                    <Mail wh={20} color={'#111827'} />
                    <span className={'sr-only'}>{t('resendInvitation')}</span>
                  </Button>
                ) : null}
                <Button
                  size={'ICON'}
                  color={'ICON'}
                  onClick={() => onEditMember(user._id)}
                >
                  <Pencil wh={20} color={'#111827'} />
                  <span className={'sr-only'}>{t('edit')}</span>
                </Button>
                <Button
                  size={'ICON'}
                  color={'ICON'}
                  onClick={() =>
                    openModal('delete_company_member', {
                      idCompany: companyInfo?._id,
                      idEmployee: user._id,
                      hasOtherAdmin: hasOtherAdminThan(user),
                    })
                  }
                >
                  <CrossIcon wh={20} color={'#111827'} />
                  <span className={'sr-only'}>{t('remove')}</span>
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SlideOverCompanyMembers;
